import { useEffect, useState } from 'react';
import styles from './styles.module.scss';
import { Table } from 'components-antd';
import { useHistory } from 'react-router-dom';
import { PreparedTransactionType } from '..';
import { Icons } from 'pages/Workshop/Icons';
import moment from 'moment';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { getUserRoleSelector } from 'store/selectors/user';
import Notifications from 'pages/Workshop/Transactions/components/Notifications/Notifications';
import { addSpaceBeforeCapitalLetters } from 'helpers';
import { resetTransactionEffect } from 'store/effects';

type TransactionsTablePropsType = {
  transactions: PreparedTransactionType[];
  searchQuery: string;
};

const TransactionsTable = (props: TransactionsTablePropsType) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { transactions, searchQuery } = props;
  const [internalTransactions, setInternalTransactions] = useState<PreparedTransactionType[]>([]);
  const userRole = useSelector(getUserRoleSelector);

  useEffect(() => {
    setInternalTransactions(transactions);
  }, [transactions.length]);

  useEffect(() => {
    handleSearchQuery();
  }, [searchQuery]);

  const handleSearchQuery = () => {
    const trimmedSearchQuery = searchQuery.trim();
    if (trimmedSearchQuery) {
      const filteredTasks = transactions.filter((task) =>
        task.SearchableString?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setInternalTransactions(filteredTasks);
    } else setInternalTransactions(transactions);
  };

  const trxClickHandler = (record) => {
    dispatch(resetTransactionEffect());
    history.push(`/workshop/transactions/${record.Id}/overview`);
  };

  const getColumns = (): any[] => {
    const columns: any[] = [
      {
        title: 'Transaction',
        key: 'Address',
        dataIndex: 'Address',
        sorter: (a, b) => {
          if (a.Address < b.Address) return -1;
          if (a.Address > b.Address) return 1;
          return 0;
        },
        render: (_, row) => {
          return (
            <div className={styles.property}>
              <div className={styles.left}>
                <Icons variant={Icons.OFFER} />
              </div>
              <div className={styles.right}>
                <div className={styles.address}>{row.Address}</div>
                {row.Location && <div className={styles.location}>{row.Location}</div>}
              </div>
            </div>
          );
        },
      },
      {
        title: 'Type',
        key: 'Type',
        dataIndex: 'Type',
        sorter: (a, b) => {
          if (a.Type < b.Type) return -1;
          if (a.Type > b.Type) return 1;
          return 0;
        },
        width: 170,
        render: (_, row) => {
          return (
            <div className={styles.trxType}>
              <div className={styles.type}>{row.Type}</div>
              <div className={styles.subType}>{row.SubType}</div>
            </div>
          );
        },
      },
      {
        title: 'Role',
        key: 'Role',
        dataIndex: 'Role',
        sorter: (a, b) => {
          if (a.Role < b.Role) return -1;
          if (a.Role > b.Role) return 1;
          return 0;
        },
        width: 200,
        render: (_, { Role }) => {
          return <div className={styles.text}>{addSpaceBeforeCapitalLetters(Role)}</div>;
        },
      },
      {
        title: 'Closing/Expires',
        key: 'ClosingExpiryDate',
        dataIndex: 'ClosingExpiryDate',
        sorter: (a, b) => {
          if (a.ClosingExpiryDate < b.ClosingExpiryDate) return -1;
          if (a.ClosingExpiryDate > b.ClosingExpiryDate) return 1;
          return 0;
        },
        width: 170,
        render: (_, { ClosingExpiryDate, IsClosedOrCancelled }) => {
          if (
            !IsClosedOrCancelled &&
            ClosingExpiryDate &&
            moment(ClosingExpiryDate).isBefore(moment(), 'day')
          ) {
            return <div className={classNames(styles.text, styles.date)}>{ClosingExpiryDate}</div>;
          } else {
            return <div className={styles.text}>{ClosingExpiryDate || '-'}</div>;
          }
        },
      },
      {
        title: '',
        key: 'Notifications',
        dataIndex: 'Notifications',
        width: 100,
        render: (_, row) => {
          return <Notifications transactionData={row} userRole={userRole} />;
        },
      },
    ];

    return columns;
  };

  return (
    <div className={styles.tableContent}>
      <Table
        className={styles.trxTable}
        bordered={false}
        columns={getColumns()}
        dataSource={internalTransactions.map((item, key) => ({
          ...item,
          key,
        }))}
        pagination={false}
        onRow={(record) => ({
          onClick: (e) => trxClickHandler(record),
        })}
      />
    </div>
  );
};

export default TransactionsTable;
